/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { environment } from "../../environments/environment";
import { useNavigate, NavLink } from "react-router-dom";
import { useAuthContext } from "../../hooks/useAuthContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import FixedBanner from "../../components/FixedBanner/FixedBanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin, faReddit } from "@fortawesome/free-brands-svg-icons";
import { FunnelIcon } from "@heroicons/react/24/outline";

const dateOpts = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  hour12: true,
};

const JobsPage = () => {
  document.title = `Scouts | LeadZLab`;

  const [jobs, setJobs] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quota, setQuota] = useState({});

  const navigate = useNavigate();
  const { user } = useAuthContext();

  const getJobs = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${environment.BACKEND}/resources/job`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      setJobs(data.data);
    } catch (err) {
      if (err.response?.status === 400) window.alert("Please login again");
    } finally {
      setLoading(false);
    }
  };

  const checkQuota = async () => {
    try {
      const { data } = await axios.get(`${environment.BACKEND}/user/quota`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      setQuota(data.data);
    } catch (err) {
      console.log(err.response?.data);
    }
  };

  useEffect(() => {
    getJobs();
    checkQuota();
  }, []);

  const handleCreateJob = () => navigate("/create-job");
  const handleCancelDelete = () => {
    setSelectedJob(null);
    setShowDeleteModal(false);
  };

  const handleDeleteJob = async () => {
    if (!selectedJob) return;
    try {
      await axios.delete(
        `${environment.BACKEND}/resources/job/${selectedJob._id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      setSelectedJob(null);
      setShowDeleteModal(false);
      getJobs();
    } catch (err) {
      console.log(err.response?.data);
    }
  };

  return (
    <div className="space-y-8">
      {/* Banners */}
      {quota?.jobsCreated >= quota?.jobsAllowed && (
        <FixedBanner
          icon={<PriorityHighIcon />}
          message={`Scout limit reached (${quota.jobsCreated}/${quota.jobsAllowed}). Upgrade to create more.`}
          actionText="Upgrade"
          actionLink="/pricing"
          color="gray"
        />
      )}
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Scouts</h1>
          <p className="mt-1 text-slate-600">Manage your scouts and see when they run.</p>
        </div>
        <button
          type="button"
          onClick={handleCreateJob}
          className="shrink-0 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          + New scout
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <Skeleton count={5} height={52} className="rounded-lg" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100">
            <FunnelIcon className="h-6 w-6 text-slate-500" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-slate-900">No scouts yet</h3>
          <p className="mt-2 max-w-sm text-slate-600">
            Create a scout to find Reddit or LinkedIn posts that match your keywords.
          </p>
          <NavLink
            to="/create-job"
            className="mt-6 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white no-underline hover:bg-emerald-700"
          >
            Create scout
          </NavLink>
        </div>
      ) : (
        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th className="col-name">Name</th>
                <th className="col-type">Type</th>
                <th className="col-date">Last run</th>
                <th className="col-action">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr
                  key={job._id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/job-detail?job_id=${job._id}`)}
                  onKeyDown={(e) => e.key === "Enter" && navigate(`/job-detail?job_id=${job._id}`)}
                  className="cursor-pointer"
                >
                  <td className="col-name font-medium text-slate-900">{job.jobName}</td>
                  <td className="col-type text-slate-600">
                    {job.type === "linkedin" && (
                      <FontAwesomeIcon icon={faLinkedin} className="text-[#0077b5]" />
                    )}
                    {job.type === "reddit" && (
                      <FontAwesomeIcon icon={faReddit} className="text-[#ff4500]" />
                    )}
                  </td>
                  <td className="col-date text-slate-600">
                    {job.lastExecuted !== "1970-01-01T00:00:00.000Z"
                      ? new Date(job.lastExecuted).toLocaleString([], dateOpts)
                      : "—"}
                  </td>
                  <td className="col-action text-right" onClick={(e) => e.stopPropagation()}>
                    <span className="inline-flex items-center justify-end gap-2">
                      <NavLink
                        to={`/job-detail?job_id=${job._id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 no-underline"
                      >
                        View
                      </NavLink>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedJob(job);
                          setShowDeleteModal(true);
                        }}
                        className="inline-flex items-center justify-center rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete modal */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40"
          onClick={handleCancelDelete}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
        >
          <div
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="delete-modal-title" className="text-xl font-bold text-slate-900">
              Delete this scout?
            </h2>
            <p className="mt-2 text-slate-600">
              This will remove this scout and all its run history. This can’t be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteJob}
                className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsPage;
